import React, { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'
import api from '../services/api'
import { Eye, EyeOff, Copy, RefreshCw, Trash2 } from 'lucide-react'

async function copyToClipboard(text, label) {
  try {
    await navigator.clipboard.writeText(text || '')
    toast.success(`${label} copied`)
  } catch {
    toast.error(`Could not copy ${label.toLowerCase()}`)
  }
}

/**
 * Manages ClickPartnerMapping rows for one campaign — the token a click
 * provider's URL carries, mapped to the credential their anonymous form
 * submission should use. Applies to click-intake campaigns and, equally,
 * to affiliate_clicks-intake campaigns whose partner also sends raw
 * browser traffic straight to our own form (intake_type only changes how
 * the SOLD conversion gets reported, not whether clicks need resolving).
 * There was no UI for this before; it was direct-DB-only. Unlike campaign
 * API keys (bcrypt-hashed, shown once on rotation), this api_key is stored
 * and returned in plaintext by design — the click-intake resolver hands it
 * to an anonymous browser on every pageview — so it's always
 * visible/copyable here, not one-time.
 */
export default function ClickPartnerMappingModal({ campaignId, campaignName, onClose }) {
  const [mappings, setMappings] = useState([])
  const [loading, setLoading] = useState(true)
  const [newToken, setNewToken] = useState('')
  const [creating, setCreating] = useState(false)
  const [busyId, setBusyId] = useState(null)
  const [revealed, setRevealed] = useState({})
  const [editingToken, setEditingToken] = useState({})
  const [confirmDeleteId, setConfirmDeleteId] = useState(null)

  const fetchMappings = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get(`/campaigns/${campaignId}/click-partner-mappings`)
      setMappings(data)
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error loading partner mappings')
    } finally {
      setLoading(false)
    }
  }, [campaignId])

  useEffect(() => { fetchMappings() }, [fetchMappings])

  const createMapping = async (e) => {
    e.preventDefault()
    const token = newToken.trim()
    if (!token) return
    setCreating(true)
    try {
      await api.post(`/campaigns/${campaignId}/click-partner-mappings`, { partner_token: token })
      toast.success('Partner token mapped')
      setNewToken('')
      fetchMappings()
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || 'Error creating mapping')
    } finally {
      setCreating(false)
    }
  }

  const saveToken = async (mapping) => {
    const token = (editingToken[mapping.id] ?? mapping.partner_token).trim()
    if (!token || token === mapping.partner_token) {
      setEditingToken(prev => { const next = { ...prev }; delete next[mapping.id]; return next })
      return
    }
    setBusyId(mapping.id)
    try {
      await api.patch(`/campaigns/${campaignId}/click-partner-mappings/${mapping.id}`, { partner_token: token })
      toast.success('Partner token updated')
      setEditingToken(prev => { const next = { ...prev }; delete next[mapping.id]; return next })
      fetchMappings()
    } catch (err) {
      toast.error(err.response?.data?.message || err.response?.data?.detail || 'Error updating token')
    } finally {
      setBusyId(null)
    }
  }

  const toggleActive = async (mapping) => {
    setBusyId(mapping.id)
    try {
      await api.patch(`/campaigns/${campaignId}/click-partner-mappings/${mapping.id}`, { is_active: !mapping.is_active })
      fetchMappings()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error updating status')
    } finally {
      setBusyId(null)
    }
  }

  const rotateKey = async (mapping) => {
    if (!window.confirm('Rotate this mapping\'s credential? The old key stops working immediately — any click currently mid-submission with it will fail.')) return
    setBusyId(mapping.id)
    try {
      await api.post(`/campaigns/${campaignId}/click-partner-mappings/${mapping.id}/rotate-key`)
      toast.success('Credential rotated')
      setRevealed(prev => ({ ...prev, [mapping.id]: true }))
      fetchMappings()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error rotating credential')
    } finally {
      setBusyId(null)
    }
  }

  const deleteMapping = async (mapping) => {
    setBusyId(mapping.id)
    try {
      await api.delete(`/campaigns/${campaignId}/click-partner-mappings/${mapping.id}`)
      toast.success('Mapping deleted')
      setConfirmDeleteId(null)
      fetchMappings()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error deleting mapping')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
        <div className="modal-header">
          <h2>Click Partner Mapping{campaignName ? ` · ${campaignName}` : ''}</h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ color: 'var(--text-secondary)', marginBottom: 20 }}>
            Maps the token a click provider's URL already carries (their <code>?source=</code>/<code>?src=</code>
            {' '}value) to this campaign's credential. The click-intake form resolves this anonymously before
            submitting — no provider-facing URL has to change to add or update a mapping here.
          </p>

          {loading ? (
            <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
          ) : (
            <>
              {mappings.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', marginBottom: 16 }}>
                  No partner token mapped to this campaign yet.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
                  {mappings.map(m => (
                    <div key={m.id} className="card" style={{ padding: 16 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                        <span className={`badge ${m.is_active ? 'badge-green' : 'badge-red'}`}>
                          {m.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            disabled={busyId === m.id}
                            onClick={() => toggleActive(m)}
                          >
                            {m.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon btn-sm"
                            title="Rotate credential"
                            disabled={busyId === m.id}
                            onClick={() => rotateKey(m)}
                          >
                            <RefreshCw size={14} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger btn-icon btn-sm"
                            title="Delete mapping"
                            disabled={busyId === m.id}
                            onClick={() => setConfirmDeleteId(m.id)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>

                      <div className="form-group" style={{ marginBottom: 10 }}>
                        <label className="form-label">Partner token</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input
                            className="input"
                            value={editingToken[m.id] ?? m.partner_token}
                            onChange={e => setEditingToken(prev => ({ ...prev, [m.id]: e.target.value }))}
                            onBlur={() => saveToken(m)}
                            onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }}
                          />
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                          Must match the literal source/src value this provider's click URL sends.
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Credential (api_key)</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input
                            className="input"
                            readOnly
                            type={revealed[m.id] ? 'text' : 'password'}
                            value={m.api_key}
                            style={{ fontFamily: 'monospace' }}
                          />
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon btn-sm"
                            title={revealed[m.id] ? 'Hide' : 'Reveal'}
                            onClick={() => setRevealed(prev => ({ ...prev, [m.id]: !prev[m.id] }))}
                          >
                            {revealed[m.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon btn-sm"
                            title="Copy"
                            onClick={() => copyToClipboard(m.api_key, 'Credential')}
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      </div>

                      {confirmDeleteId === m.id && (
                        <div style={{ marginTop: 12, padding: 12, borderRadius: 8, border: '1px solid var(--red)' }}>
                          <p style={{ margin: 0, marginBottom: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
                            Delete this mapping? Clicks carrying this token will 404 on resolve and fall back to
                            this campaign's default credentials instead.
                          </p>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                            <button className="btn btn-danger btn-sm" disabled={busyId === m.id} onClick={() => deleteMapping(m)}>
                              Delete
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <form onSubmit={createMapping} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label">Add partner token</label>
                  <input
                    className="input"
                    value={newToken}
                    onChange={e => setNewToken(e.target.value)}
                    placeholder="e.g. partnersedge"
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-sm" disabled={creating || !newToken.trim()}>
                  {creating ? <div className="spinner" /> : 'Add mapping'}
                </button>
              </form>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
                Issues a fresh production credential for this campaign and stores it on the new mapping.
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}
